import { supabase } from '../../lib/supabase';
import type { MatchPoint } from '../../types';
import { loadQueue, markSynced, replaceQueue, type QueuedPoint } from './offlineQueue';

/** Postgres unique_violation. Hit when two devices scoring the same match both picked the same next `seq` while offline. */
function isUniqueViolation(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { code?: string }).code === '23505';
}

/** Strips the queue-only `synced` flag before a point is sent to the DB. */
function toDbRow(point: QueuedPoint): MatchPoint {
  const row: Partial<QueuedPoint> = { ...point };
  delete row.synced;
  return row as MatchPoint;
}

/**
 * CRUD against the `match_points` table (sc-338 / Tennis Phase 4). Points are
 * append-only once fully tagged, but a point is flushed to the server the
 * instant it's recorded — before the post-point detail sheet adds its reason/
 * shot tags — so a point's row is mutable for a short window after its first
 * sync (the detail sheet's Save re-flushes it). `client_id` keeps a replayed
 * upsert idempotent either way. Undo is the only mutation after that window:
 * it deletes the single highest-`seq` row for the match.
 */
class MatchPointsService {
  /** All synced points for a match, in play order. */
  async getPoints(gameId: string): Promise<MatchPoint[]> {
    const { data, error } = await supabase
      .from('match_points')
      .select('*')
      .eq('game_id', gameId)
      .order('seq', { ascending: true });

    if (error) {
      console.error('[MatchPointsService] Error fetching points:', error);
      throw error;
    }
    return data || [];
  }

  /**
   * Push points to the server. Upserts on the (game_id, client_id) unique
   * constraint WITHOUT `ignoreDuplicates` — a point is flushed the instant
   * it's recorded (before the scorer has tagged it via the post-point detail
   * sheet), so a second flush for the same client_id, carrying the reason/
   * shot tags added a moment later, must overwrite the first row rather than
   * no-op on conflict. `ignoreDuplicates: true` silently dropped every tag
   * added after the point's first sync — this is what made Save in the
   * detail sheet look like it worked locally but never reach the database.
   */
  async insertPoints(points: MatchPoint[]): Promise<void> {
    if (points.length === 0) return;
    const { error } = await supabase
      .from('match_points')
      .upsert(points, { onConflict: 'game_id,client_id' });

    if (error) {
      console.error('[MatchPointsService] Error inserting points:', error);
      throw error;
    }
  }

  /** Undo: remove the single highest-seq point for this match (if it was already synced). */
  async deletePoint(gameId: string, seq: number): Promise<void> {
    const { error } = await supabase
      .from('match_points')
      .delete()
      .eq('game_id', gameId)
      .eq('seq', seq);

    if (error) {
      console.error('[MatchPointsService] Error deleting point:', error);
      throw error;
    }
  }

  /**
   * Flush this device's local offline queue to the server. Returns the
   * updated queue (synced points marked, or dropped from the "unsynced"
   * count). Safe to call repeatedly (on reconnect, after every point, on an
   * interval) — a no-op when there's nothing unsynced.
   *
   * Two-devices-same-match handling: if the insert fails on the
   * (game_id, seq) unique constraint — both devices picked the same next seq
   * while offline — this refetches the authoritative server points, drops any
   * local points that already made it to the server under a different flush,
   * renumbers the rest to start after the server's highest seq, and retries
   * once. `client_id` stays stable throughout, so this never duplicates a
   * point, only re-sequences it.
   */
  async flushQueue(gameId: string): Promise<QueuedPoint[]> {
    let queue = loadQueue(gameId);
    let unsynced = queue.filter((p) => !p.synced);
    if (unsynced.length === 0) return queue;

    try {
      await this.insertPoints(unsynced.map(toDbRow));
      return markSynced(gameId, unsynced.map((p) => p.client_id));
    } catch (e) {
      if (!isUniqueViolation(e)) {
        // Network/auth error etc. — leave queued, caller retries later.
        return queue;
      }

      const serverPoints = await this.getPoints(gameId);
      const serverClientIds = new Set(serverPoints.map((p) => p.client_id));
      const maxServerSeq = serverPoints.reduce((max, p) => Math.max(max, p.seq), 0);

      // Drop anything that actually did land on the server under a prior attempt.
      queue = loadQueue(gameId).filter((p) => p.synced || !serverClientIds.has(p.client_id));
      unsynced = queue.filter((p) => !p.synced);

      // Re-sequence the remaining unsynced points to start right after the server's current max,
      // preserving their relative local order.
      const renumbered = unsynced.map((p, i) => ({ ...p, seq: maxServerSeq + 1 + i }));
      const bySeq = new Map(renumbered.map((p) => [p.client_id, p.seq]));
      const nextQueue = queue.map((p) => (bySeq.has(p.client_id) ? { ...p, seq: bySeq.get(p.client_id)! } : p));
      replaceQueue(gameId, nextQueue);

      try {
        await this.insertPoints(renumbered.map(toDbRow));
        return markSynced(gameId, renumbered.map((p) => p.client_id));
      } catch (retryError) {
        console.error('[MatchPointsService] Error re-syncing after seq conflict:', retryError);
        return nextQueue;
      }
    }
  }
}

export const matchPointsService = new MatchPointsService();
